/// <reference types="@types/googlemaps" />
import { inject, Inject, Injectable } from '@angular/core';
import { EMPTY, Subscription } from 'rxjs';
import { DOCUMENT } from '@angular/common';

import { WorkflowService } from '../workflow/workflow.service';
import { AppInsightsService, GoogleTagManagerService, SawDust } from '@cudirect/shared-ui-library';
import { GoogleMapsService } from '../async/google-maps.service';
import { StatefulService } from '@app/service/stateful.service';
import { FieldInstance } from '@workflow/class/field.service';
import { matchDomain } from 'src/utils';
import { AnalyticsMetadataService } from '@app/service/analytics-metadata.service';

/**
 * `AddressService` facilitates address lookup functionality using Google Maps Places API.
 * It provides autocomplete functionality for address input fields and ensures the parsed address details
 * are mapped to appropriate application fields.
 *
 * TODO: We have 2 identical files here, this one is for Workflow Application - User Primary Residence Address
 * projects\cudirect-widgets\dynamic-workflow\src\lib\service\address\address.service.ts
 * This one is used on Post App Conusmer Funding. Post app should not be referencing any Workflow services as a go forward practice
 * projects\cudirect-widgets\dynamic-workflow\ecp-workflow\dynamic-app\service\address\address.service.ts
 */
@Injectable({
  providedIn: 'root'
})
export class AddressService {
  private logger = inject(SawDust).withContext({ component: 'AddressService' });

  constructor(
    private readonly workflowService: WorkflowService,
    protected readonly applicationInsightsService: AppInsightsService,
    private readonly gooleMapsService: GoogleMapsService,
    private readonly statefulService: StatefulService,
    private readonly gtm: GoogleTagManagerService,
    private readonly analyticsMetadataService: AnalyticsMetadataService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Centralized logging method to handle both ApplicationInsights and SawDust logging
   * @param eventName The name of the event to log
   * @param eventData The data to include with the event
   */
  private logEvent(eventName: string, eventData: Record<string, unknown>): void {
    this.applicationInsightsService.logEvent(eventName, eventData);
    this.logger.logEvent(eventName, eventData);
  }

  /**
   * Enables Google Places Autocomplete functionality on a provided input element and maps
   * the selected address details to either application fields or a post-application object.
   *
   * @param field - Contains information and configuration about the input field.
   * @param inputElem - HTML input element to enable autocomplete on.
   * @param isPostApp - Flag to determine if the function is being called post-application.
   * @param postAppAutoAddressConfig - Configuration mapping for post-application.
   * @param addressObj - Object to store address components during post-application phase.
   */
  public autocomplete(
    field: FieldInstance,
    inputElem: HTMLInputElement,
    isPostApp: boolean = false,
    postAppAutoAddressConfig?: string[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addressObj?: any
  ): Subscription {
    const hostname = this.getHostname();
    if (!matchDomain(/^(?:[\w-]+\.)?myori\.com$/, hostname)) {
      // Google Places Autocomplete is disabled for non-myori.com domains
      return EMPTY.subscribe(); // Return an empty subscription to avoid breaking existing clients
    }

    // Activate autocomplete for fields with `autoAddress` config or during post-application phase.
    if (field?.config?.autoAddress || isPostApp) {
      return this.gooleMapsService.getGoogleMaps().subscribe(() => {
        const autocomplete = new google.maps.places.Autocomplete(inputElem, {
          fields: ['name', 'address_components'],
          componentRestrictions: { country: 'US' },
          types: ['address']
        });

        // Event listener for when a user selects an address from dropdown suggestions.
        google.maps.event.addListener(autocomplete, 'place_changed', () => {
          this.logEvent('ecp.ui.usedGoogleAddressPrefill', {
            eventState: 'application-flow',
            status: 'success'
          });
          const data = this.analyticsMetadataService.getMetadata(this.workflowService.currentPage);
          this.gtm.publishEvent(data[0] + '-address-autocomplete', data[1]);
          // Fetch detailed information about the selected location.
          const place = autocomplete.getPlace();

          // Extract specific address parts (city, state, ZIP) and map them.
          ['locality', 'administrative_area_level_1', 'postal_code'].forEach((item, i) => {
            if (!isPostApp) {
              const fieldRef = field.config.autoAddress[i];
              if (fieldRef && typeof fieldRef === 'string') {
                const iterationId = String(field.iterationId ? field.iterationId : 0);
                const fieldId = field.config.autoAddress[i].replace('$', iterationId);
                this.statefulService.wrap(fieldId, null, field.pageIterationId).value = this.getAddrPart(place, item);
              }
            } else {
              addressObj[postAppAutoAddressConfig[i]] = this.getAddrPart(place, item);
            }
          });

          // Construct street address by combining street number and street name.
          let streetAdress = `${this.getAddrPart(place, 'street_number')} ${this.getAddrPart(place, 'route')}`;
          streetAdress = streetAdress?.trim();
          if (field.fieldMaxLength > 0 && streetAdress?.length > field.fieldMaxLength) {
            streetAdress = streetAdress.slice(0, field.fieldMaxLength);
          }

          if (streetAdress?.trim()) {
            if (!isPostApp) {
              field.data.value = streetAdress;
              // refresh primary input element with streetAddress
              inputElem.value = streetAdress;
            } else {
              addressObj.addressLine1 = streetAdress;
              inputElem.focus();
              inputElem.blur();
            }
          } else {
            // clearing existing value if no street address found
            inputElem.value = '';
            inputElem.focus();
          }
        });
      });
    }

    return EMPTY.subscribe(); // Ensures a Subscription is always returned
  }

  public unsubscribe(subscription: Subscription) {
    subscription.unsubscribe();
  }

  /**
   * Extracts a specific component of an address.
   *
   * @param place - Complete address details of a location.
   * @param name - Name of the address component to extract.
   * @returns Extracted address component or an empty string.
   */
  private getAddrPart(place: google.maps.places.PlaceResult, name: string): string {
    const component = place.address_components.find((c) => c.types[0] === name);
    return component?.[name === 'administrative_area_level_1' ? 'short_name' : 'long_name'] || '';
  }

  /**
   * Method to abstract the retrieving of hostname from the document location.
   * This is useful for testing purposes, allowing to mock the document location.
   * @returns The hostname of the current document location.
   */
  private getHostname(): string {
    return this.document.location.hostname;
  }
}
